import { createTheme, PaletteColor, SimplePaletteColorOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface TypeText {
    primary50: string;
    primary100: string;
    primary700: string;
    dark: string;
    dark2: string;
    dark3: string;
    light3: string;
    tableColor: string;
    fontLight2: string;
    fontLight3: string;
    fontLight4: string;
    gray: string;
    gray2: string;
    white: string;
    blue: string;
    green: string;
    error: string;
    red: string;
    orange: string;
    style: string;
  }
  interface Color {
    tertiary: string;
  }
  interface PaletteColor {
    text?: string;
  }
  interface SimplePaletteColorOptions {
    text?: string;
  }
  interface Palette {
    tertiary: PaletteColor;
    neutral: PaletteColor;
  }
  interface PaletteOptions {
    tertiary: SimplePaletteColorOptions;
    neutral: SimplePaletteColorOptions;
  }
}

declare module "@mui/x-data-grid" {
  interface LoadingOverlayPropsOverrides {
    length: number;
    cols: Array<any>;
  }
  interface ToolbarPropsOverrides {
    create: () => void;
  }
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1440,
      xl: 1536,
    },
  },
  spacing: 4,
  // status: {
  //   danger: "#e53e3e",
  // },
  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
  },
  palette: {
    primary: {
      main: "#37BD6B",
      dark: "#2a9553",
      light: "#3dd277",
      text: "#fff",
      contrastText: "#fff",
    },
    secondary: {
      main: "#136FD7",
      dark: "#075cbd",
      light: "#1b77de",
      text: "#fff",
      contrastText: "#C2C4C7",
    },
    tertiary: {
      main: "#FF8F0B",
      dark: "#ea8309",
      light: "#ff961b",
      text: "#fff",
      contrastText: "#fff",
    },
    neutral: {
      text: "#8A8E99",
      dark: "#272C37",
      main: "#64748B",
      contrastText: "#fff",
    },
    error: {
      main: "#FF4040",
      dark: "#E24040",
      light: "#E24040",
      text: "#fff",
      contrastText: "#fff",
    },
    success: {
      main: "#37BD6B",
      600: "#2ABA74",
    },
    warning: {
      main: "#FF8F0B",
      dark: "#e87800",
      light: "#ff9d33",
      text: "#fff",
      contrastText: "#fff",
    },
    text: {
      primary: "#212225",
      primary50: "#EFFBF3",
      primary100: "#E6F9F1",
      primary700: "#22944C",
      secondary: "#212225",
      dark: "#212225",
      dark2: "#575c66",
      dark3: "#797c86",
      light3: "#e5e7ea",
      tableColor: "#f1f2f4",
      fontLight2: "#C0C2C8",
      fontLight3: "#8a8d95",
      fontLight4: "#c2c4ca",
      gray: "#D4D6DD",
      gray2: "#A5A9B6",
      white: "#fff",
      blue: "#136FD7",
      error: "#ff4040",
      red: "#d9042e",
      green: "#2CBB4C",
      orange: "#ff9212",
      style: "#e24040",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "@global": {
          "@fontFace": ["Inter"],
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          textTransform: "capitalize",
          borderRadius: "8px",
          padding: theme.spacing(2, 3),
          ...(ownerState.variant === "contained" && ownerState.color === "primary"
            ? {
                "&": { color: theme.palette.primary.text, backgroundColor: theme.palette.primary.main },
                "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
              }
            : ownerState.variant === "contained" && ownerState.color === "secondary"
            ? {
                "&": { color: theme.palette.secondary.text, backgroundColor: theme.palette.secondary.main },
                "&.MuiLoadingButton-root.Mui-disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                },
              }
            : ownerState.variant === "contained" && ownerState.color === "secondary"
            ? {
                "&": { color: theme.palette.tertiary.text, backgroundColor: theme.palette.tertiary.main },
                "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
                "&:hover": { color: theme.palette.text.primary },
              }
            : ownerState.variant === "contained" && ownerState.color === "warning"
            ? {
                "&": { color: theme.palette.primary.text, backgroundColor: theme.palette.warning.main },
                "&.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "rgba(0, 0, 0, 0.12)" },
              }
            : ownerState.variant === "outlined" && ownerState.color === "primary"
            ? {
                "&": { color: theme.palette.primary.main, borderColor: theme.palette.primary.main },
                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                "&:hover": { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.text },
              }
            : ownerState.variant === "outlined" && ownerState.color === "secondary"
            ? {
                "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                "&:hover": { backgroundColor: theme.palette.secondary.main, color: theme.palette.secondary.text },
              }
            : ownerState.variant === "contained" && ownerState.color === "secondary"
            ? {
                "&": { color: theme.palette.secondary.main, borderColor: theme.palette.secondary.main },
                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: "transparent" },
                "&:hover": { backgroundColor: theme.palette.secondary.light },
              }
            : ownerState.variant === "contained" && ownerState.color === "error"
            ? {
                "&": { color: theme.palette.error.text, borderColor: theme.palette.error.main },
                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: theme.palette.error.main },
                "&:hover": { backgroundColor: theme.palette.error.light },
              }
            : ownerState.variant === "contained" && ownerState.color === "success"
            ? {
                "&": { color: theme.palette.text.primary700, borderColor: theme.palette.text.primary50 },
                "&, &.MuiLoadingButton-root.Mui-disabled": { backgroundColor: theme.palette.text.primary50 },
                "&:hover": { backgroundColor: theme.palette.text.primary100 },
              }
            : {
                backgroundColor: theme.palette.text.tableColor,
                color: theme.palette.text.fontLight3,
                "&:hover": { backgroundColor: theme.palette.text.fontLight2 },
              }),
          ...(ownerState.size === "large"
            ? { fontSize: "16px", lineHeight: "24px", fontWeight: 500, padding: theme.spacing(4, 8) }
            : ownerState.size === "medium"
            ? { fontSize: "16px", lineHeight: "22px", fontWeight: 600, padding: theme.spacing(4, 6) }
            : ownerState.size === "small"
            ? { fontSize: "14px", lineHeight: "21px", fontWeight: 400, padding: theme.spacing(2, 3) }
            : ownerState.size === "xsmall"
            ? { fontSize: "12px", lineHeight: "18px", fontWeight: 400, padding: theme.spacing(1, 3) }
            : {}),
        }),
      },
    },
    // MuiLoadingButton: {
    //   styleOverrides: {
    //     loadingIndicator: ({ ownerState, theme }) => ({
    //       color: "rgba(0, 0, 0, 0.76)",
    //     }),
    //   },
    // },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderColor: "#ececed",
          borderRadius: "8px",
          fontSize: "16px",
          fontWeight: 400,
          color: "#383D47",
          marginBottom: "10px",
          height: "50px",
          // "&(:not(.Mui-error)):hover fieldset.MuiOutlinedInput-notchedOutline": {
          //   borderColor: "unset",
          // },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: "1px",
            borderColor: theme.palette.text.light3,
          },
          "&:hover fieldset.MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-notchedOutline": {
            // borderColor: theme.palette.text.light3 + "!important",
            borderColor: theme.palette.text.light3,
          },
          // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          //   borderWidth: "1px",
          //   borderColor: "unset",
          // },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          marginBottom: "10px",
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: "0px",
          borderWidth: "4px",
          backgroundColor: "#ececed",
          borderColor: "#ececed",
          paddingTop: "6px",
          paddingBottom: "6px",
          "&.Mui-selected": {
            color: "#383D47",
            //backgroundColor: '#000000'
          },
        }),
      },
    },
    // MuiPickersDay: {
    //   styleOverrides: {
    //     root: ({ ownerState, theme }) => ({
    //       color: "#383D47",
    //       borderRadius: "6px",
    //       "&.Mui-selected,&.Mui-selected:hover,&:focus.Mui-selected": {
    //         backgroundColor: "#383D47",
    //         borderRadius: "6px",
    //       },
    //       "&.MuiPickersDay-today": { borderRadius: "6px" },
    //       "&:hover": { backgroundColor: "#f7f7f7" },
    //     }),
    //   },
    // },
    // MuiPickersCalendarHeader: {
    //   styleOverrides: {
    //     root: ({ ownerState, theme }) => ({
    //       color: "#383D47",
    //     }),
    //   },
    // },
    // MuiPickersYear: {
    //   styleOverrides: {
    //     root: ({ ownerState, theme }) => ({
    //       color: "#383D47",
    //       "& > .Mui-selected": { backgroundColor: "#383D47 !important" },
    //     }),
    //   },
    // },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          marginTop: "-5px",
          marginLeft: "0px",
          marginBottom: "10px",
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "&:focus-visible,fieldset:focus-visible": { outline: "none" },
        },
      },
    },
  },
});

export default theme;
